import {
  BuildOutlined,
  CloudDownloadOutlined,
  DoneAll,
  ExitToApp,
  LockOpen,
  TimerOutlined,
  Tune,
} from '@mui/icons-material'
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material'
import { useMediaQuery } from '@mui/system'
import type { TFunction } from 'i18next'
import type { User } from 'oidc-client-ts'
import type { JSX, ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'
import { Link, useLocation } from 'react-router-dom'
import logo from '@/assets/images/logo-mtinfo-cloud.svg'
import { useAuthContext } from '@/hooks/useAuthContext'
import theme, { layoutStyles } from '@/utils/theme'
import type { Location } from 'react-router-dom'

interface SidebarProps {
  open: boolean
  onClose: () => void
}

type MenuItem = {
  text: string
  icon: ReactNode
  path: string
}

interface NavigationMenuProps {
  isMobileScreen: boolean
  showContent: boolean
  onClose: () => void
  menuItems: MenuItem[]
  location: Location
  t: (key: string) => string
}

const backgroundColor = '#353232'
const ICON_COLUMN_SIZE = 48
const ICON_TEXT_GAP = 1.5

const getAvatarInitials = (userName: string): string =>
  userName
    .split(' ')
    .map((word) => word.charAt(0))
    .slice(0, 2)
    .join('')

const Sidebar = ({ open, onClose }: SidebarProps): JSX.Element => {
  const { t } = useTranslation('app')
  const { user } = useAuth()
  const { profile } = useAuthContext()
  const location = useLocation()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'))
  const [isHovering, setIsHovering] = useState(false)
  const hoverTimeoutRef = useRef<number | null>(null)
  const effectivelyOpen = open || (isHovering && !isMobileScreen)

  const [showContent, setShowContent] = useState(effectivelyOpen)

  useEffect(() => {
    let timeout: number | null = null
    if (effectivelyOpen) {
      timeout = window.setTimeout(() => setShowContent(true), 300)
    } else {
      setShowContent(false)
    }
    return () => {
      if (timeout) clearTimeout(timeout)
    }
  }, [effectivelyOpen])

  // productionOnly is false when user has pm.service privilege
  const hasServicePrivilege = !profile?.productionOnly

  const menuItems: MenuItem[] = [
    {
      text: 'app:device.intake.menuLabel',
      icon: <BuildOutlined />,
      path: '/v2/main/device/intake',
    },
    {
      text: 'app:device.calibration.menuLabel',
      icon: <Tune />,
      path: '/v2/main/device/calibration',
    },
    {
      text: 'app:device.durationTest.menuLabel',
      icon: <TimerOutlined />,
      path: '/v2/main/device/duration-test',
    },
    {
      text: 'app:device.inService.menuLabel',
      icon: <DoneAll />,
      path: '/v2/main/device/inservice',
    },
    {
      text: 'app:device.fotaJobs.menuLabel',
      icon: <CloudDownloadOutlined />,
      path: '/v2/main/device/fota-management',
    },
    ...(hasServicePrivilege
      ? [
          {
            text: 'app:device.pcbPasswordGenerator.menuLabel',
            icon: <LockOpen />,
            path: '/v2/main/device/pcb-password-generator',
          },
        ]
      : []),
  ]

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) window.clearTimeout(hoverTimeoutRef.current)
    }
  }, [])

  const handleMouseEnter = () => {
    if (!open && !isMobileScreen) {
      hoverTimeoutRef.current = window.setTimeout(
        () => setIsHovering(true),
        200,
      )
    }
  }

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      window.clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    setIsHovering(false)
  }

  const drawerContent = (
    <Box>
      <SidebarHeader showContent={showContent} t={t} />
      <SidebarDivider showContent={showContent} />
      <UserProfile showContent={showContent} user={user} />
      <LogoutButton
        isMobileScreen={isMobileScreen}
        showContent={showContent}
        onClose={onClose}
      />
      <SidebarDivider showContent={showContent} />
      <NavigationMenu
        isMobileScreen={isMobileScreen}
        onClose={onClose}
        menuItems={menuItems}
        location={location}
        t={t}
        showContent={showContent}
      />
    </Box>
  )

  return (
    <Drawer
      variant={isMobileScreen ? 'temporary' : 'permanent'}
      open={open}
      onClose={onClose}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      id="drawer"
      data-testid="sidebar-drawer"
      sx={{
        zIndex: isMobileScreen ? theme.zIndex.drawer - 1 : theme.zIndex.drawer,
        pointerEvents: open ? 'auto' : 'none',
        width: effectivelyOpen ? 260 : 64,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: effectivelyOpen ? 260 : 64,
          backgroundColor,
          color: 'white',
          overflowX: 'hidden',
          transition: theme.transitions.create('width', {
            duration: 300,
            easing: 'ease',
          }),
          borderRight: '1px solid #333',
          pointerEvents: 'auto',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  )
}

const SidebarDivider = ({ showContent }: { showContent: boolean }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 1 }}>
    <Divider
      sx={{
        backgroundColor: '#555',
        width: showContent ? '230px' : '50px',
        transition: 'width 0.3s ease',
      }}
    />
  </Box>
)

const SidebarHeader = ({
  showContent,
  t,
}: {
  showContent: boolean
  t: TFunction
}) => (
  <Toolbar
    disableGutters
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      height: 64,
      minHeight: '64px !important',
      px: 1.5,
    }}
  >
    <Box
      sx={{
        width: ICON_COLUMN_SIZE,
        minWidth: ICON_COLUMN_SIZE,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      <img
        src={logo}
        alt="Logo"
        style={{
          height: 27,
          objectFit: 'contain',
        }}
      />
    </Box>

    {/* Text section that expands/collapses */}
    <Box
      sx={{
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        width: showContent ? 'auto' : 0,
        opacity: showContent ? 1 : 0,
        transition: 'width 0.3s ease, opacity 0.2s ease',
        ml: ICON_TEXT_GAP,
      }}
    >
      {showContent && (
        <Typography
          variant="h6"
          sx={{
            ...layoutStyles.logoNormal,
            maxWidth: 180,
            whiteSpace: 'normal',
            lineHeight: 1.2,
            wordBreak: 'break-word',
          }}
        >
          {t('app:title')}
        </Typography>
      )}
    </Box>
  </Toolbar>
)
const textWrapStyles = {
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  whiteSpace: 'normal',
  lineHeight: 1.2,
}
const UserProfile = ({
  showContent,
  user,
}: {
  showContent: boolean
  user: User | null | undefined
}) => {
  const name = user?.profile?.name ?? ''
  const company =
    Array.isArray(user?.profile?.company) && user.profile.company.length > 0
      ? user.profile.company[0].name
      : ((user?.profile?.company as { name?: string })?.name ??
        'Company not available')

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 2,
        paddingLeft: '8px',
      }}
    >
      <Avatar
        sx={{
          width: ICON_COLUMN_SIZE,
          height: ICON_COLUMN_SIZE,
          flexShrink: 0,
          backgroundColor: '#ddd',
          mr: 0,
        }}
      >
        {getAvatarInitials(name)}
      </Avatar>
      <Box
        sx={{
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          width: showContent ? 'auto' : 0,
          transition: 'width 0.3s ease',
          ml: ICON_TEXT_GAP,
        }}
      >
        {showContent && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                ...textWrapStyles,
                fontWeight: 500,
              }}
            >
              {name}
            </Typography>
            <Typography variant="body2" sx={{ color: '#ddd' }}>
              {company}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}

const LogoutButton = ({
  isMobileScreen,
  showContent,
  onClose,
}: {
  isMobileScreen: boolean
  showContent: boolean
  onClose: () => void
}) => {
  const auth = useAuth()

  const handleLogout = () => {
    if (isMobileScreen) onClose()
    auth.signoutRedirect()
  }
  return (
    <Box
      component={Link}
      to="#"
      data-testid="sidebar-logout-button"
      onClick={handleLogout}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        px: 2,
        py: 2,
        color: 'white',
        textDecoration: 'none',
        transition: 'background-color 0.3s ease',
        '&.Mui-selected': {
          backgroundColor: 'rgba(0,172,193,0.9)',
          color: 'white',
        },
        '&:hover': {
          backgroundColor: 'rgba(128,128,128,0.4)',
        },
      }}
    >
      <ExitToApp
        sx={{
          width: ICON_COLUMN_SIZE,
          minWidth: ICON_COLUMN_SIZE,
          flexShrink: 0,
        }}
      />
      <Box
        sx={{
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          width: showContent ? 'auto' : 0,
          transition: 'width 0.3s ease',
          ml: ICON_TEXT_GAP,
        }}
      >
        {showContent && <Typography variant="body1">Log out</Typography>}
      </Box>
    </Box>
  )
}

const NavigationMenu = ({
  isMobileScreen,
  onClose,
  menuItems,
  location,
  t,
  showContent,
}: NavigationMenuProps & { showContent: boolean }) => (
  <List>
    {menuItems.map(({ text, icon, path }) => {
      const selected = location.pathname.startsWith(path)
      return (
        <ListItemButton
          key={text}
          data-testid={`sidebar-menu-item-${path.replaceAll('/', '-').replace(/^-/, '')}`}
          component={Link}
          to={path}
          selected={selected}
          onClick={isMobileScreen ? onClose : undefined}
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'white',
            borderRadius: 1,
            px: 1.5,
            py: 1.2,
            transition: 'background-color 0.3s ease',
            '&.Mui-selected': {
              backgroundColor: 'rgba(0,172,193,0.9)',
              color: 'white',
            },
            '&:hover': {
              backgroundColor: 'rgba(128,128,128,0.4)',
            },
          }}
        >
          <ListItemIcon
            sx={{
              width: ICON_COLUMN_SIZE,
              minWidth: ICON_COLUMN_SIZE,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: 'white',
              flexShrink: 0,
            }}
          >
            {icon}
          </ListItemIcon>
          <Box
            sx={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              width: showContent ? 'auto' : 0,
              opacity: showContent ? 1 : 0,
              transition: 'width 0.3s ease, opacity 0.15s ease',
              ml: ICON_TEXT_GAP,
            }}
          >
            <ListItemText primary={t(text)} />
          </Box>
        </ListItemButton>
      )
    })}
  </List>
)

export default Sidebar
